class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegate overlay clicks to the component root so it survives re-renders
    this.addEventListener('click', (event) => {
      if (event.target && event.target.closest && event.target.closest('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();

    // Check for cart redirect when drawer initializes
    this.checkCartRedirect();

    // Check if we need to open the cart drawer based on URL parameter
    this.checkCartOpenParameter();
  }

  // Check if cart=open parameter is in the URL and open cart drawer if it is
  checkCartOpenParameter() {
    if (window.location.search && window.location.search.includes('?cart=open')) {
      // Remove the parameter from URL without page reload
      const newUrl = window.location.pathname + window.location.search.replace('?cart=open', '').replace('&cart=open', '');
      window.history.replaceState({}, document.title, newUrl);

      // Open the cart drawer
      setTimeout(() => this.open(), 10);
    }
  }

  // Check if we should redirect from /cart page
  checkCartRedirect() {
    const isCartPage = window.location.pathname.includes('/cart');
    if (isCartPage) {
      // Check if cart redirect is enabled and URL is provided
      if (window.cartDefaults && window.cartDefaults.enableRedirect) {
        if (window.cartDefaults.urlRedirect && window.cartDefaults.urlRedirect.trim() !== '') {
          // Redirect to the specified URL
          window.location.href = window.cartDefaults.urlRedirect + '?cart=open';
          return true; // Redirect performed
        }
      }
    }
    return false; // No redirect needed
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
	  this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      // The empty notice is absent as soon as the drawer holds items, so fall back to the drawer itself.
      const containerToTrapFocusOn = (this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty')) || document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    //document.body.classList.add('overflow-hidden');
    document.querySelector('html').classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    //document.body.classList.remove('overflow-hidden');
    document.querySelector('html').classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__header'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * `is-empty` lives on several elements and each of them hides a different part of the cart.
 * Setting them from one place keeps them from drifting apart: a stale `is-empty` on
 * `cart-drawer-items` hides the line items, while a `cart-drawer` without `is-empty` hides the
 * "cart is empty" notice - together they leave the drawer completely blank.
 */
function syncCartEmptyState(itemCount) {
  const isEmpty = itemCount === 0;

  document.querySelectorAll('cart-drawer, cart-drawer-items, cart-items, #main-cart-footer').forEach((element) => {
    element.classList.toggle('is-empty', isEmpty);
  });
}

/*
 * Single entry point for re-rendering the cart drawer after a cart mutation.
 *
 * Both requests bypass the HTTP cache: a page copy cached while the cart was still empty would
 * otherwise be spliced back in, leaving the drawer blank until a manual reload.
 *
 * `cart/add.js` does not return `item_count` (it returns the line item, or `{ items: [...] }` for
 * multi-item payloads), so the empty state is always derived from `/cart.js` instead.
 */
function refreshCartDrawer() {
  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  return Promise.all([
    fetch(`${rootUrl}cart.js`, { cache: 'no-store' }).then((response) => response.json()),
    fetch(window.location.href, { cache: 'no-store' }).then((response) => response.text()),
  ])
    .then(([cart, responseText]) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const oldDrawer = document.querySelector('cart-drawer');
      const newDrawer = html.querySelector('cart-drawer');
      const oldIconCart = document.querySelector('#cart-icon-bubble');
      const newIconCart = html.querySelector('#cart-icon-bubble');

      if (oldIconCart && newIconCart) oldIconCart.innerHTML = newIconCart.innerHTML;
      if (oldDrawer && newDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;

      syncCartEmptyState(cart.item_count);

      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('rerenderCart'));
      }, 100);

      return cart;
    })
    .catch((error) => {
      console.error(error);
      return null;
    });
}

window.syncCartEmptyState = syncCartEmptyState;
window.refreshCartDrawer = refreshCartDrawer;
